import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, MapPin, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import TicketTypesManager from '@/components/TicketTypesManager';
import AccessZonesManager from '@/components/AccessZonesManager';
import EventStats from '@/components/EventStats';
import GuestListManager from '@/components/GuestListManager';
import CheckInStats from '@/components/CheckInStats';
import ScannerAuthorization from '@/components/ScannerAuthorization';
import SupportPanel from '@/components/SupportPanel';
import EventMessages from '@/components/EventMessages';
import ShareEventButton from '@/components/ShareEventButton';
import EventReminders from '@/components/EventReminders';
import InvitationsManager from '@/components/InvitationsManager';
import EventQRCodeManager from '@/components/EventQRCodeManager';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento evento');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    try {
      await axios.patch(`${API_URL}/events/${eventId}`, { status: 'published' });
      toast.success('Evento pubblicato!');
      fetchEvent();
    } catch (error) {
      toast.error('Errore durante la pubblicazione');
    }
  };

  const handleUnpublish = async () => {
    try {
      await axios.patch(`${API_URL}/events/${eventId}`, { status: 'draft' });
      toast.success('Evento rimosso dalla pubblicazione');
      fetchEvent();
    } catch (error) {
      toast.error('Errore durante la rimozione');
    }
  };

  const openPublicPage = () => {
    window.open(`/event/${eventId}`, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Caricamento...</div>
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Evento non trovato</h2>
          <Button onClick={() => navigate('/events')}>Torna agli eventi</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="event-detail-page">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/events')}
            data-testid="back-to-events-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <div className="flex gap-3">
            {event.status === 'published' && (
              <>
                <ShareEventButton event={event} variant="default" size="default" />
                <Button
                  variant="outline"
                  onClick={openPublicPage}
                  data-testid="view-public-page-button"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Pagina Pubblica
                </Button>
              </>
            )}
            {event.status === 'draft' ? (
              <Button onClick={handlePublish} data-testid="publish-event-button">
                Pubblica Evento
              </Button>
            ) : event.status === 'published' ? (
              <Button variant="outline" onClick={handleUnpublish} data-testid="unpublish-event-button">
                Rimuovi Pubblicazione
              </Button>
            ) : null}
          </div>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{event.title}</h1>
                <div className="flex gap-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    event.status === 'published' ? 'bg-green-100 text-green-800' :
                    event.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {event.status === 'published' ? 'Pubblicato' : 
                     event.status === 'draft' ? 'Bozza' : 'Chiuso'}
                  </span>
                  {event.is_public !== undefined && (
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      event.is_public ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {event.is_public ? '👁️ Pubblico' : '🔒 Privato'}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p className="text-gray-600 mb-4">{event.description}</p>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                {format(new Date(event.start_date), 'dd MMM yyyy, HH:mm', { locale: it })}
              </div>
              <div className="flex items-center text-gray-600">
                <MapPin className="w-4 h-4 mr-2" />
                {event.location}
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="tickets" className="w-full">
          <TabsList className="grid w-full grid-cols-11">
            <TabsTrigger value="tickets" data-testid="tickets-tab">Biglietti</TabsTrigger>
            <TabsTrigger value="zones" data-testid="zones-tab">Zone</TabsTrigger>
            <TabsTrigger value="guests" data-testid="guests-tab">Guest List</TabsTrigger>
            <TabsTrigger value="qrcode" data-testid="qrcode-tab">QR Locandina</TabsTrigger>
            <TabsTrigger value="stats" data-testid="stats-tab">Statistiche</TabsTrigger>
            <TabsTrigger value="checkins" data-testid="checkins-tab">Check-in</TabsTrigger>
            <TabsTrigger value="invitations" data-testid="invitations-tab">Inviti</TabsTrigger>
            <TabsTrigger value="messages" data-testid="messages-tab">Messaggi</TabsTrigger>
            <TabsTrigger value="reminders" data-testid="reminders-tab">Reminder</TabsTrigger>
            <TabsTrigger value="scanners" data-testid="scanners-tab">Scanner</TabsTrigger>
            <TabsTrigger value="support" data-testid="support-tab">Supporto</TabsTrigger>
          </TabsList>
          <TabsContent value="tickets" className="mt-6">
            <TicketTypesManager eventId={eventId} />
          </TabsContent>
          <TabsContent value="zones" className="mt-6">
            <AccessZonesManager eventId={eventId} />
          </TabsContent>
          <TabsContent value="guests" className="mt-6">
            <GuestListManager eventId={eventId} />
          </TabsContent>
          <TabsContent value="stats" className="mt-6">
            <EventStats eventId={eventId} />
          </TabsContent>
          <TabsContent value="checkins" className="mt-6">
            <CheckInStats eventId={eventId} />
          </TabsContent>
          <TabsContent value="scanners" className="mt-6">
            <ScannerAuthorization eventId={eventId} />
          </TabsContent>
          <TabsContent value="qrcode" className="mt-6">
            <EventQRCodeManager eventId={eventId} event={event} />
          </TabsContent>
          <TabsContent value="invitations" className="mt-6">
            <InvitationsManager event={event} />
          </TabsContent>
          <TabsContent value="messages" className="mt-6">
            <EventMessages eventId={eventId} />
          </TabsContent>
          <TabsContent value="reminders" className="mt-6">
            <EventReminders eventId={eventId} event={event} />
          </TabsContent>
          <TabsContent value="support" className="mt-6">
            <SupportPanel eventId={eventId} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
